// R122 — MTR → Holds: the MTR Hold Agent's home (the "Holds" list). R131: a hold plan is a card
// with many homes (one collapsible row per home); a plan made before R131 keeps its own card. Each
// home has its held bar and "opens to STR on <date>" marker, its actions and event log; New hold plan
// (Create plan → Add homes → Preview → Confirm); the agent's mode (Off / Dry run / Live — Live is
// admin-only), the daily digest (+ its Slack post, off by default), Run now and the recent runs.
// Mounted by MtrPage into #mtr-tab-body (like the Leads agent panel); kept alive across redraws.
import {
  api, errText, mhBusy, releaseSheet, sheetDirty, sheetAsk, holdFocus, esc, md, rangeLabel, whenLabel, whoLabel, nightsWord, homesWord, toneOf, pill, flagPills, boxRange, needsLook, homeAttention, homeOpens, outageOnly, isLiveHome, canPlace, writesOffWhy, draftNoteHtml, autoLine, modePill, opensLine, planDetailHtml, logHtml,
  actionsFor, runPlanAction, openGroupForm, openAddHomes, openPlaceHomes, openCancelHomes, placeableHomes, cancellableHomes, groupLine, runHeldBack, runAlertCount, MH_CSS, MODE_LABEL,
  type HoldsList, type HoldPlan, type HoldGroup, type ActionSpec, type FormHost, type HoldMode,
} from './shared';

const PANEL_CSS = `
.mh { display:flex; flex-direction:column; gap:14px; min-width:0; }
.mh-head { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
.mh-title { display:flex; align-items:center; gap:10px; flex-wrap:wrap; min-width:0; }
.mh-title h2 { margin:0; font-size:17px; font-weight:600; letter-spacing:-0.015em; color:var(--text); }
.mh-headact { display:flex; gap:8px; flex-wrap:wrap; }
.mh-headact .btn { min-height:36px; }
.mh-auto { margin:-4px 0 0; font-size:13.5px; font-weight:500; color:var(--text); line-height:1.5; }
.mh-basis { margin:-8px 0 0; font-size:12px; color:var(--text-muted); line-height:1.5; }
.mh-alerts { display:flex; flex-direction:column; gap:6px; }
.mh-alerts .mh-alert { margin:0; }
.mh-grid { display:grid; grid-template-columns:minmax(0, 1fr) 320px; gap:14px; align-items:start; }
.mh-col { display:flex; flex-direction:column; gap:14px; min-width:0; }
.mh-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:14px 16px; min-width:0; }
.mh-card h3 { margin:0 0 10px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.09em; color:var(--text-faint); display:flex; align-items:center; gap:8px; }
.mh-plans { display:flex; flex-direction:column; gap:12px; }
.mh-plan { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:14px 16px; display:flex; flex-direction:column; gap:8px; min-width:0; }
.mh-plan.mh-plan-bad { border-color:var(--danger-border); }
.mh-plan.mh-plan-warn { border-color:var(--warning-border); }
.mh-plan-h { display:flex; align-items:center; gap:8px; flex-wrap:wrap; min-width:0; }
.mh-plan-h b { font-size:14px; color:var(--text); overflow-wrap:anywhere; }
.mh-id { font-size:11px; font-weight:600; color:var(--text-muted); font-variant-numeric:tabular-nums; }
.mh-acts { display:flex; gap:8px; flex-wrap:wrap; }
.mh-plan details > summary { cursor:pointer; font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:var(--text-muted); min-height:36px; display:flex; align-items:center; list-style:none; }
.mh-plan details > summary::-webkit-details-marker { display:none; }
.mh-plan details > summary::before { content:'▸'; margin-right:6px; color:var(--text-muted); }
.mh-plan details[open] > summary::before { content:'▾'; }
.mh-plan details .mh-acts { margin-top:2px; }
.mh-acts .mh-btn-danger { min-height:36px; }
/* R131: a hold plan's card: one collapsible row per home (the Calendar page's rows are the reference) */
.mh-gset { font-size:12.5px; color:var(--text-muted); line-height:1.45; overflow-wrap:anywhere; }
.mh-gnext { font-size:12.5px; color:var(--text); line-height:1.45; overflow-wrap:anywhere; }
.mh-homes { display:flex; flex-direction:column; border:1px solid var(--border); border-radius:var(--radius-sm); min-width:0; margin-top:2px; }
.mh-plan details.mh-home { border-bottom:1px solid var(--border-soft); min-width:0; }
.mh-plan details.mh-home:last-child { border-bottom:none; }
.mh-plan details.mh-home > summary { display:flex; align-items:center; flex-wrap:wrap; gap:4px 8px; min-height:44px; padding:6px 12px; box-sizing:border-box; font-size:13px; font-weight:400; text-transform:none; letter-spacing:0; color:var(--text); min-width:0; }
.mh-home-name { display:inline-flex; align-items:baseline; gap:8px; flex-wrap:wrap; min-width:0; font-weight:600; overflow-wrap:anywhere; }
.mh-home-pills { display:inline-flex; gap:4px; flex-wrap:wrap; min-width:0; }
.mh-home-meta { margin-left:auto; font-size:12px; color:var(--text-muted); font-variant-numeric:tabular-nums; }
.mh-home-b { display:flex; flex-direction:column; gap:8px; padding:4px 12px 12px; min-width:0; }
.mh-plan details.mh-fin > summary { margin-top:2px; }
.mh-fin .mh-homes { margin-top:4px; }
.mh-run { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.mh-run .btn { min-height:36px; flex:none; }
/* the same look as .btn:disabled, with the hover and press of a live button taken off */
.mh-run .btn[aria-disabled="true"], .mh-run .btn[aria-disabled="true"]:hover, .mh-run .btn[aria-disabled="true"]:active { opacity:.6; cursor:not-allowed; transform:none; background:var(--card); border-color:var(--border); }
.mh-run span { flex:1 1 160px; min-width:0; }
.mh-modes { display:flex; gap:6px; flex-wrap:wrap; }
.mh-mode { flex:1 1 auto; min-height:36px; padding:6px 12px; border-radius:var(--radius-btn); border:1px solid var(--border); background:var(--card); color:var(--text-muted); font:inherit; font-size:12.5px; font-weight:600; cursor:pointer; }
.mh-mode.on { border-color:var(--accent); color:var(--accent); background:var(--accent-soft); }
.mh-mode:disabled, .mh-mode[aria-disabled="true"] { opacity:.5; cursor:not-allowed; }
.mh-set { display:flex; flex-direction:column; gap:8px; font-size:12px; color:var(--text-muted); line-height:1.5; }
.mh-set label.mh-check { display:flex; align-items:center; gap:8px; min-height:36px; color:var(--text); font-size:12.5px; cursor:pointer; }
.mh-set label.mh-check input { width:18px; height:18px; }
.mh-digest-t { font-weight:600; color:var(--text); font-size:12.5px; margin-bottom:6px; overflow-wrap:anywhere; }
.mh-digest ul { margin:0; padding-left:18px; display:flex; flex-direction:column; gap:3px; font-size:12px; color:var(--text); line-height:1.45; overflow-wrap:anywhere; }
.mh-runs { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:6px; font-size:12px; }
.mh-runs li { display:flex; justify-content:space-between; gap:8px; color:var(--text-muted); border-bottom:1px solid var(--border-soft); padding-bottom:6px; }
.mh-runs li:last-child { border-bottom:none; }
.mh-sheet-ovl { position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:9500; display:flex; justify-content:flex-end; }
.mh-sheet { width:min(520px, 100vw); height:100%; background:var(--card); box-shadow:var(--shadow-pop); display:flex; flex-direction:column; border-radius:var(--radius) 0 0 var(--radius); overflow:hidden; }
.mh-sheet-head { display:flex; align-items:center; justify-content:space-between; gap:8px; padding:14px 18px; border-bottom:1px solid var(--border); }
.mh-sheet-head h3 { margin:0; font-size:15px; font-weight:600; color:var(--text); overflow-wrap:anywhere; }
.mh-sheet-x { border:none; background:none; font-size:16px; color:var(--text-muted); cursor:pointer; min-width:36px; height:36px; border-radius:var(--radius-sm); }
.mh-sheet-body { flex:1; overflow-y:auto; padding:14px 18px 0; display:flex; flex-direction:column; }
.mh-toast { position:fixed; left:50%; bottom:24px; transform:translateX(-50%); z-index:9600; max-width:calc(100vw - 32px); box-sizing:border-box; padding:10px 16px; border-radius:var(--radius-btn); background:var(--text); color:var(--card); box-shadow:var(--shadow-pop); font-size:12.5px; }
@media (max-width: 900px) { .mh-grid { grid-template-columns:minmax(0, 1fr); } }
@media (max-width: 640px) {
  .mh-sheet { width:100vw; border-radius:0; }
  .mh-headact { width:100%; }
  .mh-headact .btn { flex:1 1 auto; }
  .mh-acts .btn, .mh-acts .mh-btn-danger { flex:1 1 auto; justify-content:center; }
  .mh-card, .mh-plan { padding:12px 14px; }
  .mh-home-meta { margin-left:0; flex-basis:100%; }
}
`;

/** V5, V6: how long Run now and the mode buttons stay off waiting for the list after their answer. A GET that never answers must not keep them off. */
const LIST_WAIT_MS = 15000;

export class MtrHoldsPanel {
  private root: HTMLElement | null = null;
  private data: HoldsList | null = null;
  private err = '';
  private loading = false;
  private loadsStarted = 0;
  private loadShown = 0;
  private fullLogs = new Map<string, HoldPlan>();
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private busy = '';
  /** R131 (#26): a run in flight. Never the brake's business: Off and Dry run stay pressable through a run. */
  private running = false;
  private sheetKeyOff: (() => void) | null = null;
  /** R131: what a person has expanded, by data-mh-keep; survives every redraw. */
  private keep = new Map<string, boolean>();
  /** Homes that needed attention at the last draw: one that starts needing it opens again. */
  private looked = new Set<string>();

  async mount(host: HTMLElement): Promise<void> {
    // R131 (#31): a tab switch or an auto-refresh remounts: the old DOM still knows what a person expanded
    this.keepOpenState();
    host.innerHTML = `<style>${MH_CSS}${PANEL_CSS}</style><div class="mh" data-mh-root></div>`;
    this.root = host.querySelector<HTMLElement>('[data-mh-root]');
    if (!this.root) return;
    this.root.addEventListener('click', (e) => this.onClick(e));
    this.root.addEventListener('change', (e) => this.onChange(e));
    this.draw();
    await this.load();
  }
  /** Veto the page's auto-refresh while a form is open / typed / saving. */
  suspendAutoRefresh(): boolean { return mhBusy() || !!this.busy || this.running; }
  private keepOpenState(): void {
    if (this.root) this.root.querySelectorAll<HTMLDetailsElement>('details[data-mh-keep]').forEach(x => this.keep.set(x.dataset.mhKeep || '', x.open));
  }

  private async load(): Promise<void> {
    this.loading = true;
    const seq = ++this.loadsStarted;
    const r = await api('GET', '/api/mtr/holds');
    // a GET given up on (loadForGap) can still answer: it never goes over a list asked for after it
    if (seq < this.loadShown) return;
    this.loadShown = seq;
    if (r.ok && r.j && Array.isArray(r.j.plans)) {
      this.data = r.j as HoldsList; this.err = '';
      // R122 review 2: an expanded whole log is fetched again with every load (Run now, a mode change, an
      // action, a remount), so it never shows entries older than the card above it
      const ids = [...this.fullLogs.keys()];
      if (ids.length) {
        const got = await Promise.all(ids.map(id => api('GET', `/api/mtr/holds/plans/${encodeURIComponent(id)}`)));
        ids.forEach((id, i) => { const g = got[i]; if (g.ok && g.j && g.j.plan) this.fullLogs.set(id, g.j.plan as HoldPlan); else this.fullLogs.delete(id); });
      }
    } else this.err = errText(r, 'Loading the holds');
    this.loading = false;
    this.draw();
  }
  /** The list after a press, waited for at most LIST_WAIT_MS. Past that the caller's buttons come back; the GET can still answer and draw. */
  private async loadForGap(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try { await Promise.race([this.load(), new Promise<void>(done => { timer = setTimeout(done, LIST_WAIT_MS); })]); }
    finally { if (timer) clearTimeout(timer); }
  }

  private host(): FormHost {
    const close = () => { if (this.sheetKeyOff) this.sheetKeyOff(); document.querySelector('[data-mh-sheet]')?.remove(); releaseSheet(); };
    // R131 (#18): like the Calendar's sheet: Escape, X and the dim overlay ask first when something was typed
    const askClose = () => { if (sheetDirty() && !window.confirm(sheetAsk())) return; close(); };
    return {
      today: (this.data && this.data.as_of) || new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }),
      open: (title: string, html: string) => {
        close();
        const ovl = document.createElement('div');
        ovl.className = 'mh-sheet-ovl';
        ovl.setAttribute('data-mh-sheet', '');
        ovl.innerHTML = `<style>${MH_CSS}${PANEL_CSS}</style><div class="mh-sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}"><div class="mh-sheet-head"><h3>${esc(title)}</h3><button type="button" class="mh-sheet-x" data-mh-x aria-label="Close">✕</button></div><div class="mh-sheet-body"></div></div>`;
        document.body.appendChild(ovl);
        ovl.querySelector('[data-mh-x]')?.addEventListener('click', askClose);
        ovl.addEventListener('click', (e) => { if (e.target === ovl) askClose(); });
        const onKey = (e: KeyboardEvent) => {
          if (e.key !== 'Escape') return;
          if (!ovl.isConnected) { if (this.sheetKeyOff) this.sheetKeyOff(); return; }
          // N8: a search box with something in it clears itself first (its own keydown, openAddHomes)
          const t = e.target as HTMLInputElement | null;
          if (t && t.matches && t.matches('input[type="search"]') && t.value) return;
          e.preventDefault(); e.stopPropagation();
          askClose();
        };
        document.addEventListener('keydown', onKey, true);
        this.sheetKeyOff = () => { document.removeEventListener('keydown', onKey, true); this.sheetKeyOff = null; };
        const body = ovl.querySelector<HTMLElement>('.mh-sheet-body')!;
        body.innerHTML = html;
        return body;
      },
      close,
      toast: (m: string) => this.toast(m),
      done: async () => { await this.load(); },
      groupsChanged: () => { void this.load(); },
      writesLive: () => !!(this.data && this.data.effective.writes_live),
      agent: () => (this.data ? this.data.effective : null),
    };
  }
  private toast(m: string): void {
    document.querySelector('[data-mh-toast]')?.remove();
    const t = document.createElement('div');
    t.className = 'mh-toast'; t.setAttribute('data-mh-toast', ''); t.setAttribute('role', 'status');
    t.textContent = m;
    document.body.appendChild(t);
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => t.remove(), 5000);
  }

  private plan(id: string): HoldPlan | null { return (this.data && this.data.plans.find(p => p.id === id)) || null; }

  private async onClick(e: Event): Promise<void> {
    const t = e.target as HTMLElement;
    const btn = t.closest<HTMLElement>('[data-mh-act],[data-mh-decide-act],[data-mh-new],[data-mh-run],[data-mh-mode],[data-mh-retry],[data-mh-fulllog],[data-mh-group-act],[data-mh-group-from]');
    if (!btn || !this.data && !btn.hasAttribute('data-mh-retry')) return;
    if (btn.hasAttribute('data-mh-retry')) { void this.load(); return; }
    const d = this.data!;
    if (btn.hasAttribute('data-mh-new')) { openGroupForm(this.host(), { defaults: { runway_weeks: d.config.runway_weeks, min_mtr_nights: d.config.min_mtr_nights } }); return; }
    if (btn.hasAttribute('data-mh-group-from')) {
      if (this.busy) return;
      this.busy = 'group';
      const r = await api('POST', '/api/mtr/holds/groups', { from_plan: btn.getAttribute('data-mh-group-from') });
      this.busy = '';
      if (!r.ok || !r.j.group) { this.toast(errText(r, 'Making the plan')); return; }
      openAddHomes(this.host(), r.j.group as HoldGroup);
      await this.load();
      return;
    }
    if (btn.hasAttribute('data-mh-group-act')) { await this.groupAct(btn.getAttribute('data-mh-group-act') || ''); return; }
    if (btn.hasAttribute('data-mh-run')) {
      // V5: the button keeps the keyboard through the run (aria-disabled below, and draw's holdFocus); this return is what makes a
      // second press do nothing. Nothing moves the focus when it ends: a person who moved on during the run stays where they went.
      if (this.busy || this.running) return;
      this.running = true; this.draw();
      const r = await api('POST', '/api/mtr/holds/run', { confirm: true });
      // R131 (#26): a long run answers 202 and finishes on the server; a cut connection may be the same thing, never "failed"
      const lost = r.status === 0 || r.status === 502 || r.status === 504;
      this.toast(r.j.running ? 'Still running. It finishes on its own: Last check updates when it is done.'
        : lost ? 'No answer from the run. It may still finish on its own: look at Last check in a few minutes.'
          : r.ok ? (r.j.skipped === 'off' ? 'The agent is off. Nothing was read or written.' : `Run done: ${r.j.opened || 0} opened${r.j.would_open ? `, ${r.j.would_open} would open` : ''}, ${r.j.writes || 0} write${r.j.writes === 1 ? '' : 's'}.`) : errText(r, 'The run'));
      // still "Running…" until the list is back: a press in that gap must not start a second run
      await this.loadForGap();
      this.running = false; this.draw();
      return;
    }
    if (btn.hasAttribute('data-mh-mode')) {
      const mode = btn.getAttribute('data-mh-mode') as HoldMode;
      if (mode === d.config.mode || this.busy === 'mode') return;
      if (mode === 'live' && !window.confirm('Switch the MTR hold agent to LIVE?\n\nEach morning the agent releases held nights. It only touches nights carrying a plan\'s own tag.')) return;
      this.busy = 'mode'; this.draw();
      const r = await api('POST', '/api/mtr/holds/config', { mode, ...(mode === 'live' ? { confirm: true } : {}) });
      this.toast(r.ok ? `Mode: ${MODE_LABEL[mode]}.` : errText(r, 'Changing the mode'));
      // as with a run: saving until the list is back, or a press in that gap posts the mode again
      await this.loadForGap();
      this.busy = ''; this.draw();
      return;
    }
    if (btn.hasAttribute('data-mh-fulllog')) {
      const id = btn.getAttribute('data-mh-fulllog') || '';
      const r = await api('GET', `/api/mtr/holds/plans/${encodeURIComponent(id)}`);
      if (r.ok && r.j.plan) { this.fullLogs.set(id, r.j.plan); this.draw(); }
      else this.toast(errText(r, 'Loading the log'));
      return;
    }
    // R122 review 3: the "Needs a decision" / "Lease nights to check" boxes. Review 4 (X3 / V8): each button prefills
    // ONLY its own run (data-mh-decide-run), never a span across held nights that are not in question
    if (btn.hasAttribute('data-mh-decide-act')) {
      const dp = this.plan(btn.closest<HTMLElement>('[data-mh-plan]')?.dataset.mhPlan || '');
      const dk = btn.getAttribute('data-mh-decide-act') || '';
      if (dp && ['mark_booked', 'release_now', 'cancel', 'open_lease'].includes(dk)) runPlanAction(this.host(), dp, dk as any, { range: boxRange(dp, dk, btn.getAttribute('data-mh-decide-run')) });
      return;
    }
    const [id, key] = String(btn.getAttribute('data-mh-act') || '').split('|');
    const p = this.plan(id);
    if (p && key) runPlanAction(this.host(), p, key as any);
  }
  private async groupAct(v: string): Promise<void> {
    const [id, key] = v.split('|');
    const d = this.data!;
    const g = (d.groups || []).find(x => x.id === id);
    if (!g) return;
    const homes = d.plans.filter(p => p.group_id === g.id);
    if (key === 'add') openAddHomes(this.host(), g);
    else if (key === 'place') openPlaceHomes(this.host(), g, placeableHomes(homes, d.effective.writes_live));
    else if (key === 'rename') openGroupForm(this.host(), { group: g });
    else if (key === 'cancel_all') openCancelHomes(this.host(), g, cancellableHomes(homes));
    else if (key === 'close') {
      if (this.busy) return;
      this.busy = 'group';
      const r = await api('POST', `/api/mtr/holds/groups/${encodeURIComponent(g.id)}/close`, {});
      this.busy = '';
      this.toast(r.ok ? `${g.name} is closed. Its finished homes stay under Finished.` : errText(r, 'Closing the plan'));
      await this.load();
    }
  }
  private async onChange(e: Event): Promise<void> {
    const t = e.target as HTMLInputElement;
    if (!t.matches('[data-mh-digest]') || !this.data) return;
    const r = await api('POST', '/api/mtr/holds/config', { slack_digest: t.checked });
    this.toast(r.ok ? (t.checked ? 'The daily digest will post to Slack.' : 'The daily digest stays in Dash only.') : errText(r, 'Saving'));
    await this.load();
  }

  private draw(): void {
    const root = this.root;
    if (!root) return;
    if (!this.data) {
      root.innerHTML = this.err
        ? `<div class="mh-alert mh-alert-bad">${esc(this.err)} <button type="button" class="btn btn-secondary" data-mh-retry>Retry</button></div>`
        : '<div class="mh-empty">Loading the holds…</div>';
      return;
    }
    const d = this.data;
    const eff = d.effective;
    this.keepOpenState();
    // R131 (#18): a redraw must not throw the keyboard out of the page
    const focusAfter = holdFocus();
    const groups = Array.isArray(d.groups) ? d.groups : [];
    const known = new Set(groups.map(g => g.id));
    // R131: a plan with no group (like every plan made before R131) keeps its own card
    const loose = d.plans.filter(p => !(p.group_id && known.has(p.group_id)));
    const openGroups = groups.filter(g => g.status === 'open' && !g.finished);
    const doneGroups = groups.filter(g => !(g.status === 'open' && !g.finished));
    // whatever needs a person comes first; the rest keep the server's order
    const main = [...openGroups.map(g => ({ look: g.attention > 0, html: this.groupCard(g) })), ...loose.filter(isLiveHome).map(p => ({ look: homeAttention(p), html: this.planCard(p) }))]
      .sort((x, y) => Number(y.look) - Number(x.look));
    // a finished plan that still needs a person (lease nights no tenant record covers any more, or lease nights not
    // blocked for the tenant) is listed first, all of them
    const pastAll = loose.filter(p => !isLiveHome(p));
    const past = [...doneGroups.filter(g => g.attention > 0).map(g => this.groupCard(g)), ...pastAll.filter(needsLook).map(p => this.planCard(p)), ...pastAll.filter(p => !needsLook(p)).slice(0, 10).map(p => this.planCard(p)), ...doneGroups.filter(g => !(g.attention > 0)).map(g => this.groupCard(g))];
    const canLive = !!eff.can_go_live;
    // V5, V6: a button off only for a moment stays focusable (aria-disabled, its handler returns), or the redraw would throw the
    // keyboard onto the next control. `disabled` is for what this person can never press.
    const modeBtn = (m: HoldMode) => `<button type="button" class="mh-mode${d.config.mode === m ? ' on' : ''}" data-mh-mode="${m}" aria-pressed="${d.config.mode === m}"${m === 'live' && !canLive && d.config.mode !== 'live' ? ' disabled' : this.busy === 'mode' ? ' aria-disabled="true"' : ''}${m === 'live' && !canLive ? ' title="Only an admin can switch to live"' : ''}>${MODE_LABEL[m]}</button>`;
    const lr = d.last_run;
    const auto = autoLine(eff);
    // R131 (#16): true of the mode the agent is in right now
    const releases = eff.writes_live ? 'each morning the agent releases the nights inside the STR runway, plus leftovers too short for an MTR.'
      : eff.mode === 'dry_run' ? 'in a dry run the agent only logs what it would release each morning.'
        // N9: the line above this paragraph already gives the server's reason
        : writesOffWhy(eff) ? 'nothing releases while writes are off on this server; once they are back, each morning the agent releases the nights inside the STR runway, plus leftovers too short for an MTR.'
          : 'once the agent is on, each morning it releases the nights inside the STR runway, plus leftovers too short for an MTR.';
    const lc = d.last_checked || (lr ? { at: lr.at, trigger: lr.trigger } : null);
    const byWhom = (t: string) => (t === 'cron' ? 'the daily sync' : t === 'catch_up' ? 'the catch-up' : t === 'manual' ? 'Run now' : t);
    root.innerHTML = `
      <div class="mh-head">
        <div class="mh-title"><h2>MTR holds</h2>${modePill(eff)}</div>
        <div class="mh-headact">
          <button type="button" class="btn btn-primary" data-mh-new>New hold plan</button>
        </div>
      </div>
      ${auto ? `<p class="mh-auto" data-mh-auto>${esc(auto)}</p>` : ''}
      <p class="mh-basis">A plan holds a home's MTR months in one of two ways: a long minimum stay in PriceLabs (the nights stay listed on Airbnb and Vrbo), or by blocking the nights in Hostaway. You confirm the hold; ${releases} It only touches nights still carrying the plan's own tag.</p>
      ${d.alerts.length ? `<div class="mh-alerts">${d.alerts.map(a => `<div class="mh-alert mh-alert-bad" data-mh-alert>${esc(a)}</div>`).join('')}</div>` : ''}
      <div class="mh-grid">
        <div class="mh-col">
          <div class="mh-plans" data-mh-plans>
            ${main.length ? main.map(x => x.html).join('') : `<div class="mh-card"><div class="mh-empty">No hold plans yet. Press New hold plan to hold a home's MTR months.</div></div>`}
          </div>
          ${past.length ? `<div class="mh-card" data-mh-finished><h3>Finished</h3><div class="mh-plans">${past.join('')}</div></div>` : ''}
        </div>
        <div class="mh-col">
          <section class="mh-card mh-digest" data-mh-digest-card>
            <h3>Daily digest</h3>
            ${lr ? `${lr.digest ? `<div class="mh-digest-t">${esc(lr.digest.title)}</div><ul>${(lr.digest.lines || []).map(l => `<li>${esc(l)}</li>`).join('')}</ul>` : ''}<div class="mh-empty" style="margin-top:8px">Last run ${esc(whenLabel(lr.at))} · ${esc(lr.trigger === 'cron' ? 'daily sync' : lr.trigger === 'catch_up' ? 'catch-up' : lr.trigger === 'manual' ? `Run now by ${whoLabel(lr.by)}` : lr.trigger)}</div>` : '<div class="mh-empty">No run yet. The daily sync runs it each morning (4am Pacific).</div>'}
          </section>
          <section class="mh-card" data-mh-agent="agent">
            <h3 tabindex="-1" data-mh-land>Agent</h3>
            <div class="mh-set">
              <div class="mh-modes" role="group" aria-label="Agent mode">${modeBtn('off')}${modeBtn('dry_run')}${modeBtn('live')}</div>
              <div>${eff.mode === 'off' ? 'Off: nothing runs. Plans can still be made and previewed.' : eff.mode === 'dry_run' ? 'Dry run: runs every morning. Everything is worked out and logged as "would", nothing is sent.' : eff.writes_live ? 'Live: runs every morning and releases the held nights that have reached the runway.' : esc(eff.why_not_live || '')}</div>
              <div data-mh-last-check>${lc ? `Last check: ${esc(whenLabel(lc.at))} by ${esc(byWhom(lc.trigger))}` : 'Last check: none yet'}</div>
              <div class="mh-run"><button type="button" class="btn btn-secondary" data-mh-run${this.busy || this.running ? ' aria-disabled="true"' : ''}>${this.running ? 'Running…' : 'Run now'}</button><span>Not needed day to day: this is the same check the agent runs by itself each morning.</span></div>
              ${eff.env_disabled ? '<div class="mh-alert mh-alert-bad">MTR_HOLDS_DISABLED=1 is set on the server: no Hostaway writes.</div>' : ''}
              <label class="mh-check"><input type="checkbox" data-mh-digest${d.config.slack_digest ? ' checked' : ''}${eff.can_edit_settings ? '' : ' disabled'}> Post the daily digest to Slack</label>
              <div data-mh-writes>Defaults: ${esc(d.config.runway_weeks)}-week runway · ${esc(d.config.min_mtr_nights)}-night MTR minimum · at most ${esc(d.config.max_writes_per_run)} writes a run, or 3 per home when that is more, so it grows with the number of homes.</div>
              <div>Catch-up: ${eff.timer && eff.timer.armed ? 'on (a missed morning run happens by 6am Pacific)' : `off here (${esc((eff.timer && eff.timer.reason) || 'not on Railway')})`}.</div>
              ${d.events.length ? `<div>Last change: ${esc(d.events[0].text)} · ${esc(whoLabel(d.events[0].by))}, ${esc(whenLabel(d.events[0].at))}</div>` : ''}
            </div>
          </section>
          ${d.runs.length ? `<section class="mh-card"><h3>Recent runs</h3><ul class="mh-runs">${d.runs.slice(0, 7).map(r => { const hb = runHeldBack(r); const an = runAlertCount(r); return `<li><span>${esc(md(r.day))} · ${esc(r.trigger === 'cron' ? 'daily' : r.trigger)}</span><span>${r.mode === 'live' && r.writes_live ? `${esc(r.opened)} opened` : `${esc(r.would_open)} would open`}${hb ? ` · ${esc(hb)} held back` : ''}${an ? ` · ${an} alert${an === 1 ? '' : 's'}` : ''}</span></li>`; }).join('')}</ul></section>` : ''}
        </div>
      </div>`;
    focusAfter();
  }

  /** R131: the restored open attribute of a details element. */
  private openAttr(key: string, dflt: boolean): string {
    const on = this.keep.has(key) ? this.keep.get(key) : dflt;
    return `data-mh-keep="${esc(key)}"${on ? ' open' : ''}`;
  }
  private actBtn(p: HoldPlan, a: ActionSpec, kind: ActionSpec['kind']): string {
    return `<button type="button" class="${kind === 'danger' ? 'mh-btn-danger' : `btn btn-${kind}`}" data-mh-act="${esc(p.id)}|${a.key}">${esc(a.label)}</button>`;
  }
  /** R131: visible: Mark MTR booked (the one thing a person does) and any Place action. The rest are for stepping in: the
   *  agent releases by itself, so they sit behind "Change by hand". extra: more buttons for the visible row. */
  private actsHtml(p: HoldPlan, extra = ''): string {
    const acts = actionsFor(p, this.data!.effective);
    const seen = acts.filter(a => a.key === 'place' || a.key === 'mark_booked');
    const hand = acts.filter(a => !seen.includes(a));
    return `${seen.length || extra ? `<div class="mh-acts">${seen.map(a => this.actBtn(p, a, a.kind)).join('')}${extra}</div>` : ''}
      ${hand.length ? `<details class="mh-more" ${this.openAttr(`hand:${p.id}`, false)}><summary>Change by hand</summary><div class="mh-acts">${hand.map(a => this.actBtn(p, a, a.kind === 'danger' ? 'danger' : 'secondary')).join('')}</div></details>` : ''}`;
  }
  private logDetails(p: HoldPlan): string {
    const full = this.fullLogs.get(p.id);
    const log = full ? full.log : p.log;
    return `<details data-mh-logd ${full ? 'open' : this.openAttr(`log:${p.id}`, false)}><summary>Event log</summary>${logHtml(log)}${!full && (p.log || []).length >= 6 ? `<button type="button" class="btn btn-secondary" style="margin-top:8px;min-height:36px" data-mh-fulllog="${esc(p.id)}">Show the whole log</button>` : ''}</details>`;
  }
  /** R131 (#16): a dry-run home holds nothing: it would. */
  private heldWords(p: HoldPlan): string { return p.placement === 'simulated' ? `would hold ${nightsWord(p.held_count)}` : `${nightsWord(p.held_count)} held`; }
  private planCard(p: HoldPlan): string {
    const d = this.data!;
    const admin = !!d.effective.can_go_live || d.effective.role === 'admin';
    const flag = p.overdue ? ' mh-plan-bad' : homeAttention(p) ? ' mh-plan-warn' : '';
    // R131: a plan made on its own grows into a hold plan from here (#19: not one whose window is over)
    const more = !p.group_id && isLiveHome(p) && !p.cancel_pending && p.last_night >= d.as_of ? `<button type="button" class="btn btn-secondary" data-mh-group-from="${esc(p.id)}">Add more homes to this plan</button>` : '';
    return `<article class="mh-plan${flag}" data-mh-plan="${esc(p.id)}">
      <div class="mh-plan-h"><b>${esc(p.listing_name)}</b><span class="mh-id">${esc(p.id)}</span>${pill(p.display_status, toneOf(p))}${flagPills(p)}${p.held_count ? `<span class="mh-id">${esc(this.heldWords(p))}</span>` : ''}</div>
      ${planDetailHtml(p, d.as_of, { acts: true, admin, sending: d.effective.writes_live })}
      ${draftNoteHtml(p, d.effective)}
      ${this.actsHtml(p, more)}
      ${this.logDetails(p)}
    </article>`;
  }
  /** One home of a hold plan: a row that expands to the same body a plan's own card has. A home needing attention starts open
   *  (N4: not for a PriceLabs outage, which one alert already says). */
  private homeRow(p: HoldPlan): string {
    const d = this.data!;
    const admin = !!d.effective.can_go_live || d.effective.role === 'admin';
    const look = homeOpens(p);
    if (look && !this.looked.has(p.id)) this.keep.delete(`home:${p.id}`);
    if (look) this.looked.add(p.id); else this.looked.delete(p.id);
    // R131 (#16): nothing releases with the agent off, and a dry run only says what would
    const eff = d.effective;
    const next = !p.next_open || p.status !== 'active' || p.cancel_pending ? ''
      : eff.writes_live && p.placement === 'live' ? `next ${md(p.next_open.day)}`
        : eff.mode === 'dry_run' ? `would release ${md(p.next_open.day)}` : '';
    const meta = [p.held_count ? this.heldWords(p) : '', next].filter(Boolean).join(' · ');
    return `<details class="mh-home" data-mh-home="${esc(p.id)}" data-mh-plan="${esc(p.id)}" ${this.openAttr(`home:${p.id}`, look)}>
      <summary><span class="mh-home-name">${esc(p.listing_name)}<span class="mh-id">${esc(p.id)}</span></span><span class="mh-home-pills">${pill(p.display_status, toneOf(p))}${flagPills(p)}</span>${meta ? `<span class="mh-home-meta">${esc(meta)}</span>` : ''}</summary>
      <div class="mh-home-b">
        ${planDetailHtml(p, d.as_of, { acts: true, admin, inGroup: true, sending: eff.writes_live })}
        ${this.actsHtml(p)}
        ${this.logDetails(p)}
      </div>
    </details>`;
  }
  /** R131: a hold plan: its settings, the next release, its homes (finished ones tucked away). */
  private groupCard(g: HoldGroup): string {
    const d = this.data!;
    const homes = d.plans.filter(p => p.group_id === g.id);
    const live = homes.filter(isLiveHome);
    const done = homes.filter(p => !isLiveHome(p));
    const look = (p: HoldPlan) => (homeOpens(p) ? 0 : 1);
    const closed = g.status !== 'open';
    const over = g.last_night < d.as_of;
    const flag = live.some(p => p.overdue) ? ' mh-plan-bad' : g.attention > 0 ? ' mh-plan-warn' : '';
    // R131 (#16): held means really held: a dry-run home counts apart. The next release names a home the agent would
    // really release (none with the agent off; in a dry run it says would, a home that only would hold counts too).
    const eff = d.effective;
    const dry = eff.mode === 'dry_run';
    const placedReal = live.filter(p => p.placement === 'live');
    const placedSim = live.filter(p => p.placement === 'simulated');
    const nights = (list: HoldPlan[]) => list.reduce((sum, p) => sum + (Number(p.held_count) || 0), 0);
    const heldN = nights(placedReal), wouldN = nights(placedSim);
    const pool = (eff.writes_live ? placedReal : dry ? [...placedReal, ...placedSim] : []).filter(p => p.status === 'active' && !p.cancel_pending && p.next_open);
    const nextP = pool.reduce<HoldPlan | null>((best, p) => (!best || p.next_open!.day < best.next_open!.day ? p : best), null);
    const n = nextP && nextP.next_open;
    const nextWhat = n ? (n.nights > 1 ? rangeLabel({ start: n.start, end: n.end }) : md(n.start)) : '';
    const empty = !homes.length;
    const cancels = cancellableHomes(homes);
    const btn = (key: string, label: string, cls: string) => `<button type="button" class="${cls}" data-mh-group-act="${esc(g.id)}|${key}">${esc(label)}</button>`;
    const acts = [
      !closed && !over ? btn('add', 'Add homes', 'btn btn-primary') : '',
      // #13: with the agent off, or live with writes off, Place could only save the drafts again
      !closed && g.placeable > 0 && canPlace(eff) ? btn('place', `Place ${homesWord(g.placeable)}${dry ? ' in dry run' : ''}`, 'btn btn-secondary') : '',
    ].join('');
    const drafts = !closed && g.placeable > 0 && !canPlace(eff) ? `<div class="mh-empty" data-mh-group-drafts>${esc(homesWord(g.placeable))} ${g.placeable === 1 ? 'is a draft' : 'are drafts'}: nothing is held. ${esc(writesOffWhy(eff) || `Switch the agent on to place ${g.placeable === 1 ? 'it' : 'them'}.`)}</div>` : '';
    const more = [
      closed ? '' : btn('rename', g.editable ? 'Rename or change settings' : 'Rename', 'btn btn-secondary'),
      cancels.length ? btn('cancel_all', 'Cancel whole plan', 'mh-btn-danger') : '',
      !closed && !g.homes ? btn('close', 'Close plan', 'btn btn-secondary') : '',
    ].join('');
    const status = closed ? pill('Closed', 'muted') : g.finished ? pill('Finished', 'muted') : pill(homesWord(g.homes), 'info');
    // N4: however many homes one PriceLabs outage skipped, it is one thing to look at
    const outage = homes.filter(p => homeAttention(p) && outageOnly(p)).length;
    const attN = outage ? Math.max(0, g.attention - outage) + 1 : g.attention;
    const attention = attN > 0 ? pill(`${attN} need${attN === 1 ? 's' : ''} attention`, 'warn') : '';
    return `<article class="mh-plan mh-group${flag}" data-mh-group="${esc(g.id)}">
      <div class="mh-plan-h"><b>${esc(g.name)}</b><span class="mh-id">${esc(g.id)}</span>${status}${attention}${heldN ? `<span class="mh-id">${esc(nightsWord(heldN))} held</span>` : ''}${wouldN ? `<span class="mh-id" data-mh-group-would>would hold ${esc(nightsWord(wouldN))}</span>` : ''}</div>
      <div class="mh-gset" data-mh-group-set>${esc(groupLine(g))}</div>
      ${n && nextP ? `<div class="mh-gnext" data-mh-group-next>${eff.writes_live ? 'Next release' : 'Would release'}: ${esc(md(n.day))} (${esc(nextP.listing_name)}: ${esc(nextWhat)})</div>` : ''}
      ${empty ? `<div class="mh-empty" data-mh-group-empty>${over && !closed ? "This plan's window is over. Close it from More." : closed ? 'No homes.' : 'No homes yet. Add homes to start.'}</div>` : ''}
      ${drafts}
      ${acts ? `<div class="mh-acts">${acts}</div>` : ''}
      ${more ? `<details class="mh-more" data-mh-group-more ${this.openAttr(`more:${g.id}`, false)}><summary>More</summary><div class="mh-acts">${more}</div></details>` : ''}
      ${live.length ? `<div class="mh-homes" data-mh-homes>${[...live].sort((x, y) => look(x) - look(y)).map(p => this.homeRow(p)).join('')}</div>` : ''}
      ${done.length ? `<details class="mh-fin" data-mh-group-fin ${this.openAttr(`fin:${g.id}`, done.some(homeAttention))}><summary>Finished homes (${done.length})</summary><div class="mh-homes">${[...done].sort((x, y) => look(x) - look(y)).map(p => this.homeRow(p)).join('')}</div></details>` : ''}
    </article>`;
  }
}
export { opensLine };
